import { ChangeDetectorRef, inject, Injectable, OnDestroy } from '@angular/core';
import { LocaleService } from '@libs/shared/locale';
import { Subscription } from 'rxjs';

/**
 * Base for pipes whose output depends on the current locale and needs to update live when it
 * changes - mirrors `@ngx-translate/core`'s `TranslatePipe`: subclasses must be declared `pure:
 * false` (this base can't do that itself, it's a decorator-level concern) so they're re-evaluated
 * every change-detection cycle, and this base marks the host view for check whenever the locale
 * changes so `OnPush` components pick it up even between cycles. See FA-6789.
 *
 * Angular's built-in `DatePipe`/`DecimalPipe` are deliberately NOT used here even internally -
 * they're `providedIn: 'root'` singletons that freeze `LOCALE_ID` at their own first construction,
 * same problem one level removed. Subclasses should use the `formatDate`/`formatNumber` free
 * functions from `@angular/common` instead, passing `this.locale` explicitly.
 */
@Injectable()
export abstract class ReactiveLocalePipeBase implements OnDestroy {
  protected readonly localeService = inject(LocaleService);
  protected locale: string;

  private readonly changeDetectorRef = inject(ChangeDetectorRef, { optional: true });
  private readonly localeSubscription: Subscription;

  constructor() {
    this.locale = this.localeService.currentLocale;
    this.localeSubscription = this.localeService.locale$.subscribe((locale) => {
      if (locale === this.locale) {
        return;
      }

      this.locale = locale;
      this.changeDetectorRef?.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.localeSubscription.unsubscribe();
  }
}
